import type { AiRateLimitMode } from '../../services/aiRateLimitConfig';

/**
 * One user's AI limiter override — the Phase 2 rollout mechanism.
 *
 * A handful of accounts run the daily cap while everyone else stays on the
 * credit ledger, so the caps get exercised against real usage before the global
 * flip. It overrides in both directions, so an account can also be held on
 * credits after that flip.
 *
 * ---- The amber notice is temporary and load-bearing -------------------------
 *
 * watchlock's `checkAiCap` resolves the mode from the RTDB node and returns
 * before it reads the user document, so `users/{uid}.aiRateLimitMode` is read
 * by nothing on the server today. PR 2.5 hoists that read and wires this field
 * up. Shipping the control ahead of the wiring is deliberate — but a control
 * that silently does nothing is worse than no control, so it says so.
 *
 * DELETE the notice (and the `enforced` prop) when PR 2.5 lands.
 */
export function AiRateLimitModeField({
  globalMode,
  value,
  enforced = false,
  onChange,
}: {
  /** The live global mode, or null while it is still loading. */
  globalMode: AiRateLimitMode | null;
  /** `null` = no override stored; follow the global mode. */
  value: AiRateLimitMode | null;
  /** Set to true once PR 2.5 has deployed, which removes the notice. */
  enforced?: boolean;
  onChange: (next: AiRateLimitMode | null) => void;
}) {
  const options: { key: AiRateLimitMode | null; label: string }[] = [
    { key: null, label: 'Use global' },
    { key: 'credits', label: 'Credits' },
    { key: 'daily', label: 'Daily' },
  ];

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        AI Limiter <span className="font-normal text-gray-500">(rollout override)</span>
      </label>

      <div className="flex flex-wrap items-center gap-4 mb-2">
        {options.map((opt) => (
          <label
            key={opt.key ?? 'global'}
            className="flex items-center gap-2 cursor-pointer"
          >
            <input
              type="radio"
              name="aiRateLimitMode"
              checked={value === opt.key}
              onChange={() => onChange(opt.key)}
              className="text-brand-600 focus:ring-brand-500"
            />
            <span className="text-sm text-gray-700">{opt.label}</span>
          </label>
        ))}
      </div>

      <p className="text-xs text-gray-600">
        {globalMode === null ? (
          'Loading global mode…'
        ) : (
          <>
            Global is:{' '}
            <span className="font-medium text-gray-900">{globalMode}</span>
          </>
        )}
      </p>

      {!enforced && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
          <strong>Not enforced yet.</strong> The server decides the limiter from
          the global config alone and never reads this field, so setting it has
          no effect until PR 2.5 ships. Safe to set in advance.
        </p>
      )}
    </div>
  );
}
